import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from "../components/layout/MainLayout";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Paper,
  CircularProgress,
  Tab,
  Tabs,
  Container,
  Stack,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import SendIcon from '@mui/icons-material/Send';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PeopleIcon from '@mui/icons-material/People';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { 
  fetchNotificationStatsAsync, 
  sendNotificationAsync,
  sendTestNotificationAsync,
  clearSendResult,
  clearError 
} from '../redux/reducer/notifications/notificationSlice';
import { fetchUserAsync } from '../redux/reducer/user/userSlice';

const Notifications = () => {
  const { appId } = useParams();
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState(0);
  
  // Broadcast notification state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [additionalData, setAdditionalData] = useState('');
  
  // Test notification state
  const [testUserId, setTestUserId] = useState('');
  const [testTitle, setTestTitle] = useState('');
  const [testMessage, setTestMessage] = useState('');
  
  const [sending, setSending] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);

  const stats = useSelector((state) => state.notification.stats);
  const sendResult = useSelector((state) => state.notification.sendResult);
  const error = useSelector((state) => state.notification.error);
  const users = useSelector((state) => state.user.list);

  useEffect(() => {
    dispatch(fetchNotificationStatsAsync(appId));
    dispatch(fetchUserAsync(appId));
  }, [dispatch, appId]);

  useEffect(() => {
    if (sendResult) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
        dispatch(clearSendResult());
      }, 5000);
    }
  }, [sendResult, dispatch]);

  useEffect(() => {
    if (error) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
        dispatch(clearError());
      }, 5000);
    }
  }, [error, dispatch]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Autocomplete filter: search by name, email, or id
  const filterUsers = createFilterOptions({
    stringify: (option) => `${option?.name || ''} ${option?.email || ''} ${option?._id || ''}`
  });

  const handleSendBroadcast = async () => {
    if (!title || !message) {
      alert('Title and message are required');
      return;
    }

    setSending(true);
    try {
      let data = null;
      if (additionalData) {
        try {
          data = JSON.parse(additionalData);
        } catch (e) {
          alert('Invalid JSON in additional data');
          setSending(false);
          return;
        }
      }

      await dispatch(sendNotificationAsync(appId, { title, message, data }));
      
      // Clear form
      setTitle('');
      setMessage('');
      setAdditionalData('');
      
      // Refresh stats
      dispatch(fetchNotificationStatsAsync(appId));
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!testUserId || !testTitle || !testMessage) {
      alert('User ID, title, and message are required');
      return;
    }

    setSending(true);
    try {
      await dispatch(sendTestNotificationAsync(appId, { 
        userId: testUserId, 
        title: testTitle, 
        message: testMessage 
      }));
      
      // Clear form
      setTestUserId('');
      setTestTitle('');
      setTestMessage('');
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <MainLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <NotificationsActiveIcon 
              sx={{ 
                fontSize: 40,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }} 
            />
            <Box>
              <Typography
                variant="h4"
                fontWeight="700"
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Push Notifications
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Send notifications to your app users
              </Typography>
            </Box>
          </Stack>
        </Box>
      
      {/* Success Alert */}
      {successAlert && sendResult && (
        <Alert severity="success" onClose={() => setSuccessAlert(false)}>
          {sendResult.message}
          {sendResult.stats && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Sent: {sendResult.stats.totalSent || 0} | Failed: {sendResult.stats.failed || 0}
            </Typography>
          )}
        </Alert>
      )}

      {/* Error Alert */}
      {errorAlert && error && (
        <Alert severity="error" onClose={() => setErrorAlert(false)}>
          {error}
        </Alert>
      )}

        {/* Statistics Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <PeopleIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                  {stats.totalUsers}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                <PhoneAndroidIcon sx={{ fontSize: 40, color: '#4caf50' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                  {stats.usersWithTokens}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Devices
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
                <PhoneAndroidIcon sx={{ fontSize: 40, color: '#f44336' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                  {stats.usersWithoutTokens}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Inactive Devices
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                <NotificationsActiveIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
                  {stats.percentage}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Reachability
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Tabs for Broadcast and Test */}
        <Card>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Broadcast Notification" />
            <Tab label="Test Notification" />
          </Tabs>

          {/* Broadcast Notification Tab */}
          {tabValue === 0 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Send Notification to All Users
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This will send a push notification to all users who have the app installed and have enabled notifications.
              </Typography>
              
              <TextField
                label="Notification Title"
                fullWidth
                margin="normal"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="e.g., New Feature Available!"
              />
              
              <TextField
                label="Notification Message"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                placeholder="e.g., Check out our new course materials..."
              />
              
              <TextField
                label="Additional Data (Optional JSON)"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                value={additionalData}
                onChange={(e) => setAdditionalData(e.target.value)}
                placeholder='{"courseId": "123", "action": "view_course"}'
                helperText="Enter valid JSON data to pass additional information"
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handleSendBroadcast}
                  disabled={sending || !title || !message}
                  size="large"
                >
                  {sending ? 'Sending...' : 'Send Broadcast'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTitle('');
                    setMessage('');
                    setAdditionalData('');
                  }}
                  disabled={sending}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}

          {/* Test Notification Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Send Test Notification to Single User
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Send a test notification to a specific user for testing purposes.
              </Typography>

              <Autocomplete
                options={Array.isArray(users) ? users : []}
                loading={!users}
                value={(Array.isArray(users) ? users : []).find((u) => u._id === testUserId) || null}
                onChange={(e, value) => setTestUserId(value?._id || '')}
                getOptionLabel={(option) => option ? `${option.name || ''}${option.email ? ' - ' + option.email : ''}` : ''}
                filterOptions={filterUsers}
                sx={{ width: '100%', mt: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="User"
                    margin="normal"
                    placeholder="Search by name or email..."
                    helperText="Start typing to search users"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name || 'Unnamed'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email || 'No email'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ ml: 'auto', color: option.firebaseDeviceToken ? 'success.main' : 'text.disabled' }}>
                        {option.firebaseDeviceToken ? 'Has token' : 'No token'}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
              
              <TextField
                label="Test Notification Title"
                fullWidth
                margin="normal"
                value={testTitle}
                onChange={(e) => setTestTitle(e.target.value)}
                required
                placeholder="Test Notification"
              />
              
              <TextField
                label="Test Notification Message"
                fullWidth
                margin="normal"
                multiline
                rows={3}
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                required
                placeholder="This is a test notification"
              />

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  onClick={handleSendTest}
                  disabled={sending || !testUserId || !testTitle || !testMessage}
                  size="large"
                >
                  {sending ? 'Sending...' : 'Send Test'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTestUserId('');
                    setTestTitle('');
                    setTestMessage('');
                  }}
                  disabled={sending}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}
        </Card>

        {/* Info Section */}
        <Paper sx={{ p: 2, mt: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Note:</strong> Notifications will only be delivered to users who have:
            <ul>
              <li>Installed the app</li>
              <li>Granted notification permissions</li>
              <li>An active Firebase device token</li>
            </ul>
          </Typography>
        </Paper>
      </Container>
    </MainLayout>
  );
};

export default Notifications;
